import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import AlertModal from "@/components/ui/AlertModal.web";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import { WA_CONSENT_VERSION } from "@/constants/waConsent";
import { useAuth } from "@/context/AuthContext";
import { UserDocument } from "@/types/user";
import { computeRenewalDate } from "@/utils/computeRenewalDate";
import { useFormatDate, useFormatDateTime } from "@/utils/formatDate";
import { toDate } from "@/utils/toDate";
import Ionicons from "@expo/vector-icons/Ionicons";
import { router } from "expo-router";
import Head from "expo-router/head";
import { getApp } from "firebase/app";
import {
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet, Switch } from "react-native";

const firestore = getFirestore(getApp());

type FormData = {
  waPostNotificationsEnabled: boolean;
  globalFeedPushEnabled: boolean;
};

const dateToTimestampLike = (d: Date) => ({
  seconds: Math.floor(d.getTime() / 1000),
  nanoseconds: 0,
});

const SettingsNotificationsPage = () => {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const { formatDateTime } = useFormatDateTime();
  const [isInitialized, setIsInitialized] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [consentModal, setConsentModal] = useState<{
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);
  const lastSavedWaRef = useRef<boolean | null>(null);
  const lastSavedGlobalFeedRef = useRef<boolean | null>(null);

  const { control, reset, watch } = useForm<FormData>({
    defaultValues: {
      waPostNotificationsEnabled: false,
      globalFeedPushEnabled: false,
    },
  });

  const waPostNotificationsEnabled = !!watch("waPostNotificationsEnabled");
  const globalFeedPushEnabled = !!watch("globalFeedPushEnabled");

  const hasPhoneProvider =
    user?.providerData?.some((p) => p?.providerId === "phone") ?? false;
  const isFirestoreVerified = userData?.phoneNumber?.verified === true;
  const isPhoneVerified = hasPhoneProvider && isFirestoreVerified;

  const renewalDate = useMemo(() => {
    const anchor =
      toDate(userData?.waPlanStartedAt) ?? toDate(userData?.createdAt);
    return computeRenewalDate(anchor, new Date());
  }, [userData?.waPlanStartedAt, userData?.createdAt]);

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      const userDocRef = doc(firestore, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data() as UserDocument;
        const initialWa = !!data.waPostNotificationsEnabled;
        const initialGlobalFeed =
          !!data.notificationSettings?.globalFeedPosts;
        reset({
          waPostNotificationsEnabled: initialWa,
          globalFeedPushEnabled: initialGlobalFeed,
        });
        lastSavedWaRef.current = initialWa;
        lastSavedGlobalFeedRef.current = initialGlobalFeed;
      }
      setIsInitialized(true);
    };

    fetchUserData();
  }, [user, reset]);

  useEffect(() => {
    if (!user || !isInitialized) return;
    if (lastSavedWaRef.current === waPostNotificationsEnabled) return;

    const timeoutId = setTimeout(async () => {
      try {
        const userDocRef = doc(firestore, "users", user.uid);
        const payload: Record<string, unknown> = {
          waPostNotificationsEnabled,
        };
        if (waPostNotificationsEnabled) {
          // Record which version of the consent disclosure the user accepted
          // — used by the re-consent notice to detect users on stale terms.
          payload.waConsentVersion = WA_CONSENT_VERSION;
          if (userData?.recipientMonthlyWAQuota == null) {
            payload.recipientMonthlyWAQuota = 1;
          }
          if (userData?.waPlanStartedAt == null) {
            payload.waPlanStartedAt = serverTimestamp();
          }
        }
        await setDoc(userDocRef, payload, { merge: true });

        lastSavedWaRef.current = waPostNotificationsEnabled;
        reset({
          waPostNotificationsEnabled,
          globalFeedPushEnabled,
        });
      } catch (error) {
        console.error("Error saving notifications settings:", error);
        setModalInfo({ title: t("ERROR"), message: t("NETWORK_ERROR") });
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [
    waPostNotificationsEnabled,
    globalFeedPushEnabled,
    user,
    isInitialized,
    reset,
    t,
    userData?.recipientMonthlyWAQuota,
    userData?.waPlanStartedAt,
  ]);

  useEffect(() => {
    if (!user || !isInitialized) return;
    if (lastSavedGlobalFeedRef.current === globalFeedPushEnabled) return;

    const timeoutId = setTimeout(async () => {
      try {
        const userDocRef = doc(firestore, "users", user.uid);
        await setDoc(
          userDocRef,
          {
            notificationSettings: {
              globalFeedPosts: globalFeedPushEnabled,
            },
          },
          { merge: true },
        );

        lastSavedGlobalFeedRef.current = globalFeedPushEnabled;
        reset({
          waPostNotificationsEnabled,
          globalFeedPushEnabled,
        });
      } catch (error) {
        console.error("Error saving global-feed notification setting:", error);
        setModalInfo({ title: t("ERROR"), message: t("NETWORK_ERROR") });
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [
    globalFeedPushEnabled,
    waPostNotificationsEnabled,
    user,
    isInitialized,
    reset,
    t,
  ]);

  // Re-consent path: user is currently subscribed but their stored consent
  // version predates the current disclosure. Show a passive notice with a
  // Review button that opens the same consent modal used at first enable;
  // on confirm we do a one-off write to bump the stored version.
  const needsReconsent =
    !!userData?.waPostNotificationsEnabled &&
    (userData?.waConsentVersion ?? 0) < WA_CONSENT_VERSION;

  const openReconsentModal = () => {
    setConsentModal({
      onConfirm: async () => {
        setConsentModal(null);
        if (!user) return;
        try {
          const userDocRef = doc(firestore, "users", user.uid);
          await setDoc(
            userDocRef,
            { waConsentVersion: WA_CONSENT_VERSION },
            { merge: true },
          );
        } catch (error) {
          console.error("Error saving consent version:", error);
          setModalInfo({ title: t("ERROR"), message: t("NETWORK_ERROR") });
        }
      },
      onCancel: () => setConsentModal(null),
    });
  };

  const showStatusCard = isPhoneVerified && waPostNotificationsEnabled;
  const lastReceivedDate = toDate(userData?.recipientMonthlyWAUsage);
  const lastReceivedText = lastReceivedDate
    ? formatDateTime(dateToTimestampLike(lastReceivedDate))
    : t("WA_NOTIFICATIONS_NEVER");
  const renewsOnText = renewalDate
    ? formatDate(dateToTimestampLike(renewalDate))
    : null;

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_SETTINGS_NOTIFICATIONS")}</title>
      </Head>
      <CustomHeader
        title={t("NOTIFICATIONS")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={() => router.replace("/(tabs)/settings")}
      />
      <ThemedView style={styles.container}>
        <ThemedView style={styles.toggleRow}>
          <ThemedView style={styles.toggleLabelContainer}>
            <ThemedText style={styles.toggleTitle}>
              {t("GLOBAL_FEED_NOTIFICATIONS")}
            </ThemedText>
            <ThemedText style={styles.toggleHint}>
              {t("GLOBAL_FEED_NOTIFICATIONS_HINT")}
            </ThemedText>
          </ThemedView>
          <Controller
            control={control}
            name="globalFeedPushEnabled"
            render={({ field: { onChange, value } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                testID="global-feed-switch"
              />
            )}
          />
        </ThemedView>

        <ThemedView style={styles.divider} />

        <ThemedView style={styles.toggleRow}>
          <ThemedView style={styles.toggleLabelContainer}>
            <ThemedText style={styles.toggleTitle}>
              {t("WA_POST_NOTIFICATIONS")}
            </ThemedText>
            <ThemedText style={styles.toggleHint}>
              {t("WA_POST_NOTIFICATIONS_HINT")}
            </ThemedText>
          </ThemedView>
          <Controller
            control={control}
            name="waPostNotificationsEnabled"
            render={({ field: { onChange, value } }) => (
              <Switch
                testID="wa-switch"
                value={!!value && isPhoneVerified}
                onValueChange={(next) => {
                  // First-time enable → surface the WA data-sharing consent
                  // before flipping the form value. On cancel, we simply don't
                  // call onChange, so the toggle stays visually off and the
                  // debounced write never fires.
                  const isFirstEnable =
                    next === true && !userData?.waPlanStartedAt;
                  if (isFirstEnable) {
                    setConsentModal({
                      onConfirm: () => {
                        setConsentModal(null);
                        onChange(true);
                      },
                      onCancel: () => setConsentModal(null),
                    });
                    return;
                  }
                  onChange(next);
                }}
                disabled={!isPhoneVerified}
              />
            )}
          />
        </ThemedView>

        {!isPhoneVerified && (
          <ThemedView style={styles.verifyHintContainer}>
            <ThemedText style={styles.verifyHintText}>
              {t("WA_NOTIFICATIONS_VERIFY_PHONE_HINT")}
            </ThemedText>
            <Pressable
              onPress={() =>
                router.push("/settings-signin-methods" as never)
              }
              hitSlop={8}
            >
              <ThemedText style={styles.verifyHintCta}>
                {t("WA_NOTIFICATIONS_VERIFY_PHONE_CTA")}
              </ThemedText>
            </Pressable>
          </ThemedView>
        )}

        {needsReconsent && (
          <ThemedView style={styles.reconsentNotice}>
            <ThemedText style={styles.reconsentText}>
              {t("WA_RECONSENT_NOTICE")}
            </ThemedText>
            <Pressable onPress={openReconsentModal} hitSlop={8}>
              <ThemedText style={styles.reconsentCta}>
                {t("WA_RECONSENT_CTA")}
              </ThemedText>
            </Pressable>
          </ThemedView>
        )}

        {showStatusCard && (
          <ThemedView style={styles.statusCard}>
            <ThemedView style={styles.statusRow}>
              <ThemedText style={styles.statusLabel}>
                {t("WA_NOTIFICATIONS_LEFT_THIS_MONTH")}
              </ThemedText>
              <ThemedText style={styles.statusValue}>
                {String(userData?.recipientMonthlyWAQuota ?? 0)}
              </ThemedText>
            </ThemedView>
            <ThemedView style={styles.statusRow}>
              <ThemedText style={styles.statusLabel}>
                {t("WA_NOTIFICATIONS_LAST_RECEIVED")}
              </ThemedText>
              <ThemedText style={styles.statusValue}>
                {lastReceivedText}
              </ThemedText>
            </ThemedView>
            {renewsOnText && (
              <ThemedView style={styles.statusRow}>
                <ThemedText style={styles.statusLabel}>
                  {t("WA_NOTIFICATIONS_RENEWS_ON")}
                </ThemedText>
                <ThemedText style={styles.statusValue}>
                  {renewsOnText}
                </ThemedText>
              </ThemedView>
            )}
          </ThemedView>
        )}

        <Pressable
          onPress={() => setDetailsOpen((v) => !v)}
          style={styles.detailsHeader}
          hitSlop={8}
        >
          <ThemedText style={styles.detailsHeaderText}>
            {t("WA_NOTIFICATIONS_MORE_DETAILS")}
          </ThemedText>
          <Ionicons
            name={detailsOpen ? "chevron-up" : "chevron-down"}
            size={16}
            color="#6B7280"
          />
        </Pressable>
        {detailsOpen && (
          <ThemedView style={styles.detailsBody}>
            <ThemedText style={styles.detailsBodyText}>
              {t("WA_NOTIFICATIONS_FREE_PLAN_NOTICE")}
            </ThemedText>
          </ThemedView>
        )}
      </ThemedView>
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
        >
          <ThemedView>
            <ThemedText>{modalInfo.message}</ThemedText>
            <Button
              text={t("OK")}
              onPress={() => setModalInfo(null)}
              style={{ marginTop: 20 }}
            />
          </ThemedView>
        </CustomModal>
      )}
      {consentModal && (
        <AlertModal
          visible={true}
          onClose={consentModal.onCancel}
          title={t("WA_ENABLE_CONFIRM_TITLE")}
          message={t("WA_ENABLE_CONFIRM_BODY")}
          primaryAction={{
            text: t("WA_ENABLE_CONFIRM_CTA"),
            onPress: consentModal.onConfirm,
          }}
          secondaryAction={{
            text: t("CANCEL"),
            onPress: consentModal.onCancel,
          }}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 10,
  },
  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginTop: 20,
    marginBottom: 4,
  },
  toggleLabelContainer: {
    flex: 1,
  },
  toggleTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  toggleHint: {
    fontSize: 13,
    marginTop: 4,
    opacity: 0.7,
  },
  verifyHintContainer: {
    marginTop: 8,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  verifyHintText: {
    fontSize: 13,
    opacity: 0.75,
    flexShrink: 1,
  },
  verifyHintCta: {
    fontSize: 13,
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  reconsentNotice: {
    marginTop: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "#FFE4A3",
    backgroundColor: "#FFF7E6",
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  reconsentText: {
    fontSize: 13,
    color: "#7A4A00",
    flexShrink: 1,
  },
  reconsentCta: {
    fontSize: 13,
    fontWeight: "700",
    color: "#204169",
    textDecorationLine: "underline",
  },
  statusCard: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    gap: 8,
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  statusLabel: {
    fontSize: 13,
    opacity: 0.75,
    flexShrink: 1,
  },
  statusValue: {
    fontSize: 13,
    fontWeight: "600",
    textAlign: "right",
  },
  detailsHeader: {
    marginTop: 20,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  detailsHeaderText: {
    fontSize: 13,
    fontWeight: "600",
    opacity: 0.8,
  },
  detailsBody: {
    padding: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
  },
  detailsBodyText: {
    fontSize: 13,
    opacity: 0.8,
    lineHeight: 18,
  },
});

export default SettingsNotificationsPage;
